
import { supabase, invokeFunction } from '@/integrations/supabase/client';

// Table name transformation function - matches create-csv-database and query-organisation-data logic
export const transformTableName = (fileName: string): string => {
  // Remove file extension
  const safeFileName = fileName.replace(/\.[^/.]+$/, "");

  // Generate a unique and SQL-safe table name
  const tableName = safeFileName
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_') // Replace non-alphanumeric characters with underscores
    .replace(/^[0-9]/, 'n$&') // Prefix with 'n' if first character is a number
    .replace(/_+/g, '_') // Replace multiple underscores with a single one
    .substring(0, 63); // Ensure name is not too long for Postgres

  return tableName;
};

const isEcommerceCsvConnection = (connectionId?: string, connection?: any): boolean => {
  const candidateId = String(connectionId || connection?.connectionId || connection?.id || '');
  const isCsv = candidateId.startsWith('csv-import-');
  const isInventoryCsvImport = candidateId.includes('_inventory_');
  return Boolean(
    isCsv &&
    !isInventoryCsvImport &&
    connection?.import_type === 'ecommerce' &&
    typeof connection?.ecommerce_subtype === 'string' &&
    connection.ecommerce_subtype.trim().length > 0
  );
};

// Check if a table name follows our naming convention
export const isValidTableName = (tableName: string): boolean => {
  // Must be lowercase, contain only letters, numbers, and underscores
  const validPattern = /^[a-z][a-z0-9_]*$/;

  // Skip system tables and special tables
  const systemTables = ['csv_imports', 'csv_table_registry'];
  if (systemTables.includes(tableName)) {
    return true;
  }


  return validPattern.test(tableName);
};

// Helper to identify inventory snapshot connections
export const isInventoryConnection = (connection: any): boolean => {
  if (!connection) return false;

  if (isEcommerceCsvConnection(connection?.connectionId || connection?.id, connection)) {
    return false;
  }
  
  // Check various indicators that this is an inventory connection
  const id = connection.id || connection.connectionId || '';
  const name = connection.name || '';
  const description = connection.description || '';
  
  return (
    id.includes('_inventory_') ||
    name.toLowerCase().includes('inventory') ||
    description.toLowerCase().includes('inventory')
  );
};

// Helper function to determine the correct database name based on connection type
// effectiveOrgId: pass the source org's ID for test clones to use the correct DB name
export const getDbNameForConnection = (
  connectionId: string,
  organisationId?: string,
  connection?: any,
  effectiveOrgId?: string
): string => {
  const isEcommerceCsv = isEcommerceCsvConnection(connectionId, connection);

  // CRITICAL: Check connectionId string for inventory pattern FIRST
  // This handles cases where expanded CSV import IDs contain '_inventory_'
  // even when the connection object lookup failed (null/undefined)
  if (connectionId.includes('_inventory_') && !isEcommerceCsv) {
    return 'snapshots';
  }

  // Check if this is an inventory connection via connection object
  if (!isEcommerceCsv && isInventoryConnection(connection)) {
    return 'snapshots';
  }

  // For ecommerce CSV imports, use the effective org ID (source org for clones)
  const resolvedOrgId = effectiveOrgId ?? organisationId;
  if (connectionId.startsWith('csv-import-')) {
    if (connection?.import_type === 'ecommerce' && connection?.ecommerce_subtype && resolvedOrgId) {
      const subtype = connection.ecommerce_subtype.toLowerCase();
      const sanitizedOrgId = resolvedOrgId.replace(/-/g, '_');
      return `figabl_ecommerce_${subtype}_org_${sanitizedOrgId}`;
    }
    // Fallback for non-ecommerce CSV imports
    return `figabl_${connectionId.replace(/[^a-zA-Z0-9]/g, '_')}`;
  }

  // Some Fivetran connectors provide an explicit destination database.
  // Prefer it over the derived fallback to avoid querying the wrong DB.
  const metadataDbName = connection?.metadata?.originalItem?.database;
  if (typeof metadataDbName === 'string' && metadataDbName.trim().length > 0) {
    const trimmedDbName = metadataDbName.trim();
    if (/^[a-zA-Z0-9_]+$/.test(trimmedDbName)) {
      return trimmedDbName;
    }
  }

  // Non-CSV connections are keyed by connectionId for DB naming.
  return `figabl_${String(connectionId).replace(/-/g, '_')}`;
};

// Get the inventory snapshot table name for an organization
export const getInventorySnapshotTableName = (organisationId: string): string => {
  const sanitizedOrgId = organisationId.replace(/-/g, '_');
  return `org_${sanitizedOrgId}_inventory_snapshots`;
};

export const fetchOrganisationData = async (
  connectionId: string,
  schema?: string,
  tableName?: string,
  options: {
    page?: number;
    pageSize?: number;
    filters?: Array<{ column: string; value: string; operation: string }>;
    sortColumn?: string;
    sortDirection?: 'asc' | 'desc';
    organisationId?: string;
    connection?: any;
    effectiveOrgId?: string;
    dataCutoffDate?: string;
  } = {}
): Promise<any[]> => {
  try {
    const { page = 1, pageSize = 100, filters = [], sortColumn, sortDirection, organisationId, connection, effectiveOrgId, dataCutoffDate } = options;

    // Determine database name — for test clones, use source org's DB
    const dbName = getDbNameForConnection(connectionId, organisationId, connection, effectiveOrgId);

    // Cutoff date filtering is handled server-side in query-organisation-data
    // (only applied to inherited source-org data, not clone-owned imports)
    const effectiveFilters = filters;

    // Validate table name before making request
    if (tableName && !isValidTableName(tableName)) {
      console.error(`Invalid table name: ${tableName}`);
      throw new Error(`Table "${tableName}" does not follow valid naming convention`);
    }
    // Set extended timeout for csv_imports and csv-related tables
    const isCSVImportsTable = tableName === 'csv_imports';
    const isCSVRelatedTable = tableName?.toLowerCase().includes('csv_') ||
      schema?.toLowerCase().includes('csv');

    // Adjust timeout and limit based on table type
    const queryTimeout = isCSVImportsTable ? 120000 : // 2 minutes for csv_imports
      isCSVRelatedTable ? 90000 :  // 1.5 minutes for csv-related tables
        30000; // 30 seconds for regular tables

    // Lower row limit for CSV tables to prevent timeouts
    const rowLimit = isCSVImportsTable ? 100 :
      isCSVRelatedTable ? 200 :
        500;

    // Always use pagination for better performance
    const response = await invokeFunction(supabase, 'query-organisation-data', {
      body: {
        dbName,
        connectionId,
        schema: schema || "public",
        tableName,
        rowLimit,
        queryTimeout,
        usePagination: true,
        page,
        pageSize,
        filters: effectiveFilters,
        sortColumn,
        sortDirection,
        organisationId,
        // Add a flag for retry logic in the edge function
        enableRetry: isCSVImportsTable || isCSVRelatedTable
      }
    });

    if (response.error) {
      console.error('Error fetching organization data:', response.error);
      throw new Error(`Error fetching organization data: ${response.error.message}`);
    }

    if (!response.data || !response.data.success) {
      if (response.data?.tables) {
        return [];
      }

      // Enhanced error handling for timeout errors
      if (response.data?.errorType === 'timeout' ||
        (response.data?.error && response.data.error.includes('statement timeout'))) {
        console.error(`Query timeout occurred for ${schema}.${tableName}:`, response.data?.error);

        // Provide more helpful error message based on the table type
        if (isCSVImportsTable) {
          throw new Error(
            `The CSV imports table query timed out. This table contains all raw CSV data and can be large. Please use a filtered query, try the csv_table_registry table instead, or access individual imported CSV tables directly.`
          );
        } else if (isCSVRelatedTable) {
          throw new Error(
            `The query on table "${schema}.${tableName}" timed out. Try applying filters, reducing page size, or accessing a different table.`
          );
        } else {
          throw new Error(
            `The query on table "${schema}.${tableName}" timed out. Try applying filters or using a smaller page size.`
          );
        }
      }

      console.error('Failed to fetch organization data:', response.data?.error || 'No data returned');
      throw new Error(`Failed to fetch organization data: ${response.data?.error || 'No data returned'}`);
    }

    // Attach pagination data to the returned records array
    const records = response.data.records || [];

    if (response.data.pagination) {
      Object.defineProperty(records, 'pagination', {
        value: response.data.pagination,
        enumerable: false
      });
    }

    return records;
  } catch (error) {
    console.error('Error fetching organization data:', error);
    throw error;
  }
};

export const fetchAvailableTables = async (
  connectionId: string,
  organisationId?: string,
  connection?: any,
  filterByConnectionId: boolean = true,
  effectiveOrgId?: string
): Promise<any[]> => {
  try {
    const isEcommerceCsv = isEcommerceCsvConnection(connectionId, connection);

    // Check if this is an inventory connection - check ID pattern first, then connection object
    // This handles expanded CSV imports where connectionId contains '_inventory_'
    const isInventory = !isEcommerceCsv && (connectionId.includes('_inventory_') || isInventoryConnection(connection));

    if (isInventory && (effectiveOrgId ?? organisationId)) {
      // For test clones, use the source org's snapshot table (clone has no own table)
      const inventoryTableName = getInventorySnapshotTableName(effectiveOrgId ?? organisationId ?? '');
      // Use the connection's file name or a clean display name
      const displayName = connection?.file_name 
        ? `${connection.file_name.replace(/\.[^/.]+$/, '')} - Inventory Snapshots`
        : 'Inventory Snapshots';
      console.log(`Inventory connection detected (id pattern: ${connectionId.includes('_inventory_')}, obj: ${isInventoryConnection(connection)}), returning snapshot table: ${inventoryTableName} (display: ${displayName})`);
      return [{
        table_schema: 'public',
        table_name: inventoryTableName,
        display_name: displayName
      }];
    }
    
    // Determine database name based on connection type
    const dbName = getDbNameForConnection(connectionId, organisationId, connection, effectiveOrgId);

    const response = await invokeFunction(supabase, 'query-organisation-data', {
      body: {
        dbName,
        connectionId,
        listTables: true,
        filterByConnectionId
      }
    });

    if (response.error) {
      console.error(`Error fetching available tables: ${response.error.message}`);
      return []
    }

    if (!response.data || !response.data.success) {
      console.error(`Failed to fetch available tables: ${response.data?.error || 'No tables data returned'}`);
      return []
    }

    const tables = response.data.tables || [];

    // Filter out any tables that don't follow our naming convention
    const validTables = tables.filter((table: any) => {
      const tableName = table.table_name;
      return isValidTableName(tableName);
    });

    return validTables;
  } catch (error) {
    console.error('Error fetching available tables:', error);
    throw error;
  }
};

export const findDefaultTable = (
  tables: any[],
  connectionId?: string,
  registryData?: Array<{ table_name: string; connection_id: string }>
): { schema: string, tableName: string } | null => {
  if (!tables || tables.length === 0) {
    return null;
  }

  // Filter to only valid tables
  const validTables = tables.filter((table: any) => isValidTableName(table.table_name));

  // If we have registry data and a connectionId, find the matching table directly
  if (connectionId && registryData && registryData.length > 0) {
    const registryEntry = registryData.find(r => r.connection_id === connectionId);
    if (registryEntry) {
      const matchingTable = validTables.find(t => t.table_name === registryEntry.table_name);
      if (matchingTable) {
        return {
          schema: matchingTable.table_schema,
          tableName: matchingTable.table_name
        };
      }
    }
  }

  // When we have a connectionId, prioritize actual data tables over system tables
  // This is because the tables list should already be filtered by connectionId
  if (connectionId) {
    const dataTables = validTables.filter(
      table => !['csv_table_registry', 'csv_imports'].includes(table.table_name.toLowerCase())
    );
    
    if (dataTables.length > 0) {
      // Return the first actual data table for this connection
      return {
        schema: dataTables[0].table_schema,
        tableName: dataTables[0].table_name
      };
    }
  }

  // Fallback: check for csv_table_registry - best entry point for CSV data browsing
  const csvTableRegistry = validTables.find(
    table => table.table_name.toLowerCase() === 'csv_table_registry'
  );

  if (csvTableRegistry) {
    return {
      schema: csvTableRegistry.table_schema,
      tableName: csvTableRegistry.table_name
    };
  }

  // Avoid csv_imports table due to known timeout issues
  const csvImportsTable = validTables.find(
    table => table.table_name.toLowerCase() === 'csv_imports'
  );

  if (csvImportsTable && validTables.length > 1) {
    // If csv_imports is the only table, we'll return it, but if there are other tables,
    // we'll skip it to avoid timeout issues
    const otherTable = validTables.find(table => table.table_name.toLowerCase() !== 'csv_imports');
    if (otherTable) {
      return {
        schema: otherTable.table_schema,
        tableName: otherTable.table_name
      };
    }
  }

  // Second priority: look for order-related tables
  const exactOrderTable = validTables.find(
    table => ['order'].includes(table.table_name.toLowerCase())
  );

  if (exactOrderTable) {
    return {
      schema: exactOrderTable.table_schema,
      tableName: exactOrderTable.table_name
    };
  }

  // Third priority: look for order-related tables with similar names
  const orderRelatedTable = validTables.find(
    table => {
      const tableName = table.table_name.toLowerCase();
      return (tableName.includes('order') || tableName.includes('sale')) && !tableName.includes('_');
    }
  );

  if (orderRelatedTable) {
    return {
      schema: orderRelatedTable.table_schema,
      tableName: orderRelatedTable.table_name
    };
  }

  // Fourth priority: fall back to product tables if no order tables found
  const exactProductTable = validTables.find(
    table => ['product', 'products'].includes(table.table_name.toLowerCase())
  );

  if (exactProductTable) {
    return {
      schema: exactProductTable.table_schema,
      tableName: exactProductTable.table_name
    };
  }

  // Fifth priority: product-related tables with similar names
  const productRelatedTable = validTables.find(
    table => {
      const tableName = table.table_name.toLowerCase();
      return tableName.includes('product') && !tableName.includes('_');
    }
  );

  if (productRelatedTable) {
    return {
      schema: productRelatedTable.table_schema,
      tableName: productRelatedTable.table_name
    };
  }

  // Final fallback: use the first valid table available that's not csv_imports
  const firstNonCsvImportsTable = validTables.find(
    table => table.table_name.toLowerCase() !== 'csv_imports'
  );

  if (firstNonCsvImportsTable) {
    return {
      schema: firstNonCsvImportsTable.table_schema,
      tableName: firstNonCsvImportsTable.table_name
    };
  }

  // If no other options, use the first valid table
  if (validTables.length > 0) {
    return {
      schema: validTables[0].table_schema,
      tableName: validTables[0].table_name
    };
  }

  return null;
};
